'use client';

import { useTranslation } from 'react-i18next';

import { publicStorageUrl } from '@kit/shared/storage';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { Eyebrow } from '~/(tenant)/_components/native/_primitives';

const PERIOD_LABEL_KEYS: Record<string, string> = {
  monthly: 'common:memberClub.memberships.card.periodMonthly',
  quarterly: 'common:memberClub.memberships.card.periodQuarterly',
  semi_annual: 'common:memberClub.memberships.card.periodSemiAnnual',
  annual: 'common:memberClub.memberships.card.periodAnnual',
  lifetime: 'common:memberClub.memberships.card.periodLifetime',
};

const PERIOD_LABEL_DEFAULTS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-annual',
  annual: 'Annual',
  lifetime: 'Lifetime',
};

export function useMembershipPeriodLabel(period: string) {
  const { t } = useTranslation();
  const key = PERIOD_LABEL_KEYS[period];

  return key
    ? t(key, { defaultValue: PERIOD_LABEL_DEFAULTS[period] ?? '' })
    : '';
}

/**
 * Top of a membership plan card: a 16:9 hero image with the plan's accent
 * rail, or just the rail when the plan has no image. Shared by the catalog
 * card and the payment page so a plan looks the same wherever it appears.
 * Must be rendered inside a `position: relative; overflow: hidden` card.
 */
export function MembershipPlanHero({
  name,
  imageUrl,
  accent,
}: {
  name: string;
  imageUrl: string | null;
  accent: string;
}) {
  const t = useNativeTokens();

  if (!imageUrl) {
    return (
      <div
        style={{
          position: 'absolute',
          top: 22,
          bottom: 22,
          insetInlineStart: 0,
          width: 2,
          background: accent,
        }}
      />
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        background: `${accent}14`,
        borderBottom: `1px solid ${t.line}`,
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={publicStorageUrl(imageUrl)}
        alt={name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
      <div
        style={{
          position: 'absolute',
          insetInlineStart: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: accent,
        }}
      />
    </div>
  );
}

/** "What's included" checklist. Renders nothing for an empty list. */
export function MembershipPlanBenefits({
  benefits,
  accent,
}: {
  benefits: string[];
  accent: string;
}) {
  const t = useNativeTokens();

  if (benefits.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: 18 }}>
      <Eyebrow size="sm" style={{ marginBottom: 10 }}>
        <Trans
          i18nKey="common:memberClub.memberships.purchase.benefitsHeading"
          defaults="What's included"
        />
      </Eyebrow>
      {benefits.map((line, i) => (
        <div
          key={i}
          dir="auto"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '6px 0',
            fontFamily: t.fontSans,
            fontSize: 13,
            color: t.foreground,
            lineHeight: 1.45,
          }}
        >
          <span
            aria-hidden
            style={{
              color: accent,
              marginTop: 2,
              flexShrink: 0,
              fontWeight: 500,
            }}
          >
            ✓
          </span>
          <span>{line}</span>
        </div>
      ))}
    </div>
  );
}
